import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getErrors, getIsLoading } from '@perpay-web/data-module/useDataModule';
import {
    analyticsSSICardApplicationClickChangeEmployer,
    analyticsSSICardApplicationClickContinue,
    analyticsSSICardApplicationViewModal,
} from '@perpay-web/fintech/actions/analytics/SSIcardApplication';
import {
    analyticsPartnerHostedOnboardingApplicationClickAgreement,
    analyticsPartnerHostedOnboardingApplicationClickConsent,
    analyticsPartnerHostedOnboardingApplicationClickDirectDeposit,
    analyticsPartnerHostedOnboardingApplicationClickPatriot,
    analyticsPartnerHostedOnboardingApplicationClickPrivacyNotice,
    analyticsPartnerHostedOnboardingApplicationClickPrivacyPolicy,
    analyticsPartnerHostedOnboardingApplicationClickRewards,
    analyticsPartnerHostedOnboardingApplicationClickSubmitApplication,
    analyticsPartnerHostedOnboardingApplicationClickTransfersAgreement,
    analyticsPartnerHostedOnboardingApplicationCloseAutoTransferModal,
    analyticsPartnerHostedOnboardingApplicationOpenAutoTransferModal,
    analyticsPartnerHostedOnboardingApplicationToggleAcceptAll,
    analyticsPartnerHostedOnboardingApplicationToggleAutoApply,
    analyticsPartnerHostedOnboardingApplicationToggleCardHolderAgreement,
    analyticsPartnerHostedOnboardingApplicationToggleCreditReportingAgreement,
    analyticsPartnerHostedOnboardingApplicationToggleReceiveText,
    analyticsPartnerHostedOnboardingApplicationViewEmployerInfoModal,
    analyticsPartnerHostedOnboardingApplicationViewValueProp,
    analyticsPartnerHostedOnboardingClickDasboard,
    analyticsPartnerHostedOnboardingSelectMaterialClickHeavyMetal,
    analyticsPartnerHostedOnboardingSelectMaterialClickLightPlastic,
    analyticsPartnerHostedOnboardingStartApplication,
    analyticsPartnerHostedOnboardingUnsupportedEmployer,
} from '@perpay-web/fintech/actions/analytics/partnerHostedOnboarding';
import { submitCardApplication } from '@perpay-web/fintech/actions/entities/cardAccountApplications';
import CardApplicationEmploymentDetails from '@perpay-web/fintech/components/composite/CardApplicationEmploymentDetails/CardApplicationEmploymentDetails';
import { SELF_ONBOARDED_COMPANY_NAME } from '@perpay-web/fintech/constants/companies';
import {
    fetchJobsDataModule,
    getCompanyName,
    getCompanyUuid,
    getEstimatedNetPay,
    getIsGigEconomyCompany,
    getIsPerpaySplitEligible,
    getPayCycle,
} from '@perpay-web/fintech/dataModules/fetchJobs';
import { getIsPhoneVerified } from '@perpay-web/fintech/dataModules/fetchUserInfo';
import { useSubmitCardApplicationContext } from '@perpay-web/fintech/hooks/dataModule/useSubmitCardApplicationContext';
import { useMetalCardABTest } from '@perpay-web/hooks/abTests/useMetalCardABTest';
import { centsToDollar, dollarToCents } from '@perpay-web/utils/numberUtils';

const transformData = (values, hasSelectedMaterial, skipPhoneInfo = false) => {
    const {
        dateOfBirth,
        ssn,
        payCycle,
        acceptedCardHolderAgreement,
        optedInToAutoApply,
        selectedCompany,
        selfOnboardingData,
        material,
    } = values;
    return {
        userInfo: {
            dateOfBirth,
            ssn,
        },
        jobInfo: {
            company: {
                company: selectedCompany.company,
                uuid: selectedCompany.uuid,
            },
            isGigEconomyCompany: selectedCompany.isGigEconomyCompany,
            estimatedNetPay: dollarToCents(values.estimatedNetPay),
            payCycle,
            ...(selfOnboardingData && { selfOnboardingData }),
        },
        cardApplicationInfo: {
            acceptedCardHolderAgreement,
            acceptedFairCreditReportingAct:
                values.acceptedFairCreditReportingAgreement,
            optedInToAutoApply,
            ...(hasSelectedMaterial && { material }),
        },
        phoneInfo: skipPhoneInfo
            ? null
            : {
                  tcpaOptIn: values.receiveText,
              },
    };
};

const CardApplicationEmploymentDetailsContainer = ({
    isExistingUserWithPassword,
    isUserIdentityVerified,
    partnerCode,
    welcomeOffer,
    isUserInPremiumGroup,
}) => {
    const dispatch = useDispatch();
    const submitCardApplicationDataModule = useSubmitCardApplicationContext();
    const errors = {
        ...getErrors(submitCardApplicationDataModule.state),
        ...useSelector(fetchJobsDataModule.getErrors),
    };

    const isPhoneVerified = useSelector(getIsPhoneVerified);
    const hasPerpaySplitPayroll = useSelector(getIsPerpaySplitEligible);
    const isGigEconomyCompany = useSelector(getIsGigEconomyCompany);
    const companyName = useSelector(getCompanyName);
    const companyUuid = useSelector(getCompanyUuid);
    const estimatedNetPay = useSelector(getEstimatedNetPay);
    const currentPayCycle = useSelector(getPayCycle);

    const isSelfOnboarded = companyName === SELF_ONBOARDED_COMPANY_NAME;

    const shouldPrefillCompanyData = !(
        isSelfOnboarded ||
        hasPerpaySplitPayroll ||
        isGigEconomyCompany
    );

    const onSSIModalMount = useCallback(() => {
        dispatch(analyticsSSICardApplicationViewModal());
    }, [dispatch]);

    const onSSIModalChangeEmployer = useCallback(() => {
        dispatch(analyticsSSICardApplicationClickChangeEmployer());
    }, [dispatch]);

    const onSSIModalContinue = useCallback(() => {
        dispatch(analyticsSSICardApplicationClickContinue());
    }, [dispatch]);

    const currentCompanyInfo = shouldPrefillCompanyData
        ? {
              company: companyName,
              uuid: companyUuid,
          }
        : {
              company: '',
              uuid: '',
          };

    const currentEstimatedNetPay = estimatedNetPay
        ? centsToDollar(estimatedNetPay)
        : '';

    const isJobsLoading = useSelector(
        fetchJobsDataModule.getIsLoadingOrUnrequested,
    );
    const isLoading = isExistingUserWithPassword ? isJobsLoading : false;
    const isSubmitLoading = getIsLoading(submitCardApplicationDataModule.state);

    const { isUserInTestGroup: isMetalCardEnabled } = useMetalCardABTest();

    const cardMaterialChoiceExperience = isUserInPremiumGroup
        ? 'premium'
        : 'base';

    const handleOnMount = useCallback(() => {
        dispatch(analyticsPartnerHostedOnboardingStartApplication());
        if (isExistingUserWithPassword) {
            dispatch(fetchJobsDataModule.dataRequest());
        }
    }, [dispatch, isExistingUserWithPassword]);

    return (
        <CardApplicationEmploymentDetails
            isExistingUserWithPassword={isExistingUserWithPassword}
            partnerCode={partnerCode}
            welcomeOffer={welcomeOffer}
            errors={errors}
            onMount={handleOnMount}
            currentEstimatedNetPay={currentEstimatedNetPay}
            currentPayCycle={currentPayCycle}
            currentCompanyInfo={currentCompanyInfo}
            isLoading={isLoading}
            isSubmitLoading={isSubmitLoading}
            onSubmit={(values) => {
                const transformedData = transformData(
                    values,
                    isMetalCardEnabled || isUserInPremiumGroup,
                    isPhoneVerified,
                );
                dispatch(submitCardApplication(transformedData));
            }}
            isUserIdentityVerified={isUserIdentityVerified}
            isPhoneVerified={isPhoneVerified}
            isMetalCardEnabled={isMetalCardEnabled}
            isUserInPremiumGroup={isUserInPremiumGroup}
            onClickHeavyMetal={() => {
                dispatch(
                    analyticsPartnerHostedOnboardingSelectMaterialClickHeavyMetal(
                        cardMaterialChoiceExperience,
                    ),
                );
            }}
            onClickLightPlastic={() => {
                dispatch(
                    analyticsPartnerHostedOnboardingSelectMaterialClickLightPlastic(
                        cardMaterialChoiceExperience,
                    ),
                );
            }}
            analyticsOnClickSubmitApplication={(payload) =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickSubmitApplication(
                        payload,
                    ),
                )
            }
            analyticsOnOpenAutoTransferModal={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationOpenAutoTransferModal(),
                )
            }
            analyticsOnCloseAutoTransferModal={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationCloseAutoTransferModal(),
                )
            }
            analyticsOnViewEmployerInfoModal={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationViewEmployerInfoModal(),
                )
            }
            analyticsOnViewValueProp={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationViewValueProp(),
                )
            }
            onClickTransfersAgreement={() => {
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickTransfersAgreement(),
                );
            }}
            onClickAgreement={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickAgreement(),
                )
            }
            onClickRewards={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickRewards(),
                )
            }
            onClickDirectDeposit={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickDirectDeposit(),
                )
            }
            onClickConsent={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickConsent(),
                )
            }
            onClickPrivacyPolicy={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickPrivacyPolicy(),
                )
            }
            onClickPrivacyNotice={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickPrivacyNotice(),
                )
            }
            onClickPatriot={() =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationClickPatriot(),
                )
            }
            onToggleAutoApply={(values) => {
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationToggleAutoApply(
                        values,
                    ),
                );
            }}
            onToggleCreditReportingAgreement={(value) =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationToggleCreditReportingAgreement(
                        value,
                    ),
                )
            }
            onToggleCardHolderAgreement={(value) =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationToggleCardHolderAgreement(
                        value,
                    ),
                )
            }
            onToggleReceiveText={(value) =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationToggleReceiveText(
                        value,
                    ),
                )
            }
            onToggleAcceptAll={(value) =>
                dispatch(
                    analyticsPartnerHostedOnboardingApplicationToggleAcceptAll(
                        value,
                    ),
                )
            }
            onCompanySelect={(value) => {
                if (
                    !value.isCardEligibleSsi &&
                    (value.isGigEconomyCompany || value.hasPerpaySplitPayroll)
                ) {
                    dispatch(
                        analyticsPartnerHostedOnboardingUnsupportedEmployer(),
                    );
                }
            }}
            onClickDashboard={() =>
                dispatch(analyticsPartnerHostedOnboardingClickDasboard())
            }
            onSSIModalMount={onSSIModalMount}
            onSSIModalChangeEmployer={onSSIModalChangeEmployer}
            onSSIModalContinue={onSSIModalContinue}
        />
    );
};

export default CardApplicationEmploymentDetailsContainer;
